'use client';

import { createContext, useContext, type FC, type ReactNode } from 'react';
import { useNavigation as useNavigationHook } from '../hooks/useNavigation';

type NavigationType = ReturnType<typeof useNavigationHook>;

export const NavigationContext = createContext<NavigationType | undefined>(undefined);

export const NavigationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const navigation = useNavigationHook();

  return <NavigationContext.Provider value={navigation}>{children}</NavigationContext.Provider>;
};

/** Outside a NavigationProvider (anonymous landing, SSR prerender) the
 * navigation surface falls back to a no-op — any call is silently dropped
 * and `state` points at a benign default. Screens rendered on the public
 * landing (e.g., DesktopHeader) do not rely on real navigation. */
const NO_OP_NAVIGATION = {
  state: {
    activeTab: 'cases',
    stacks: {
      balance: [{ screen: 'balance' }],
      cases: [{ screen: 'cases' }],
      games: [{ screen: 'games' }],
      inventory: [{ screen: 'inventory' }],
      referrals: [{ screen: 'referrals' }],
      profile: [{ screen: 'profile' }],
      onboarding: [{ screen: 'onboarding' }],
      shop: [{ screen: 'shop' }],
    },
  },
  changeTab: () => {},
  pushScreen: () => {},
  replaceScreen: () => {},
  popScreen: () => {},
  resetStack: () => {},
  goBack: () => {},
  navigate: () => {},
  getCurrentScreen: () => ({ screen: 'cases' }),
  canGoBack: () => false,
  updateCurrentParams: () => {},
} as unknown as NavigationType;

export const useNavigation = (): NavigationType => {
  const context = useContext(NavigationContext);
  return context ?? NO_OP_NAVIGATION;
};
